import React, { useEffect, useState } from 'react';
import Page from 'app/components/misc/BasePage/Blog';
import BlogLayout from 'app/components/blog/BlogLayout';
import { MobileOnly, DesktopOnly } from 'app/components/misc/Layout';
import SubscriptionForm from 'app/components/blog/SubscriptionForm';
import {
  ArticleDetail,
  RelatedArticleSection,
  Head,
  AmpAnalytics,
  StickyActionBar,
  ViewNonAmpLink,
} from 'components/blog/article';
import styled from 'styled-components';
import BlogSubHeader from 'components/blog/BlogSubHeader';
import { useAmp } from 'next/amp';
import { media } from 'theme';
import { mapBlogData } from 'entity/blog/blogUtils';
import getShareCount from 'utils/getShareCount';
import { ArticleWithLink } from 'entity/blog/blogTypes';
import blogApi from 'entity/blog/blogApi';
import PopularSection from '../../../components/blog/PopularSection';

const SectionItem = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  > div {
    width: 100%;
  }
`;

const SectionHeader = styled.div`
  color: ${props => props.theme.colors.neutral7};
  font-family: 'Open Sans';
  font-size: 28px;
  padding: 32px 16px;

  ${media.largeAndUp`
    padding: 32px 0px;
  `}
`;

interface OwnProps {
  id: number;
  slug: string;
  wpnonce: string;
}

/**
 * Article page is a hybrid AMP page.
 * We have to ensure that there are no errors in terminal
 * when on article page in AMP mode in development
 * (by adding ?amp=1 to the URL)
 */
const Article = (props: OwnProps) => {
  const { id, wpnonce, slug } = props;

  const [relatedArticles, setRelatedArticles] = useState([]);
  const [article, setArticle] = useState<ArticleWithLink>();

  const isAmp = useAmp();

  useEffect(() => {
    const getArticlePreview = async () => {
      const articleResponse = await blogApi.getArticlePreviewFromWp({
        id,
        wpnonce,
      });
      if (articleResponse.ok) {
        const articleWithLink = mapBlogData(articleResponse.data);
        setArticle(articleWithLink);
      }
    };
    getArticlePreview();
  }, [id, wpnonce]);

  // get related Articles
  useEffect(() => {
    const fetchRelatedArticles = async () => {
      const articleResponse = await blogApi.getRelatedArticles(slug);
      if (articleResponse.ok) {
        setRelatedArticles(articleResponse?.data.posts);
      }
    };
    fetchRelatedArticles();
  }, [slug]);

  if (!article) {
    return <div>Loading...</div>;
  }

  const shareCount = getShareCount(article.shares);
  return (
    <Page renderSubHeader={() => <BlogSubHeader />}>
      <Head article={article} />
      {!isAmp && (
        <DesktopOnly>
          <StickyActionBar
            slug={article.slug}
            path={article.link.as}
            shareCount={shareCount}
          />
        </DesktopOnly>
      )}
      <BlogLayout stickySidebarDisabled>
        <AmpAnalytics />
        <SectionItem>
          <ArticleDetail article={article} />
        </SectionItem>
        {!isAmp && (
          <MobileOnly>
            {relatedArticles.length > 0 && (
              <SectionItem>
                <SectionHeader>Related Articles</SectionHeader>
                <RelatedArticleSection articles={relatedArticles} />
              </SectionItem>
            )}
            <SectionItem>
              <SubscriptionForm />
            </SectionItem>
            <SectionItem>
              <PopularSection />
            </SectionItem>
          </MobileOnly>
        )}
        <DesktopOnly>
          {relatedArticles.length > 0 && (
            <SectionItem>
              <SectionHeader>Related Articles</SectionHeader>
              <RelatedArticleSection articles={relatedArticles} />
            </SectionItem>
          )}
        </DesktopOnly>
        <ViewNonAmpLink slug={article.link.as} />
      </BlogLayout>
    </Page>
  );
};

Article.getInitialProps = async ({ query }) => {
  return {
    id: query.articleSlug,
    slug: query.articleSlug,
    wpnonce: query.wpnonce,
  };
};

export const config = { amp: 'hybrid' };
export default Article;
